import {Component, OnInit} from '@angular/core';
import {GroupColumn} from '../../_models/OrdiniGroupColumns';
import {ModalController} from '@ionic/angular';
import {AccountOrganizzazione} from '../../_models/AccountOrganizzazione';
import {SaveMessage} from '../../_helpers/save-message';
import {OrganizzazioniService} from '../../_services/organizzazioni.service';
import {CampiUtenteService} from '../../_services/campi-utente.service';
import {CampoUtenteDef} from '../../_models/CampoUtenteDef';
import {getOvColumnMeta} from '../../_models/ordine-vendita-list-columns';

// Feature 064 — colonna selezionabile arricchita con la categoria (per il raggruppamento).
interface SelectableColumn {
  columnName: string;
  viewName: string;
  category: string;
}

interface CategoryGroup {
  category: string;
  columns: SelectableColumn[];
}

const LOCK_COLUMN = 'RifOrdine';

@Component({
  selector: 'app-ordini-vendita-show-columns',
  templateUrl: './ordini-vendita-show-columns.component.html',
  styleUrls: ['./ordini-vendita-show-columns.component.scss'],
  standalone: false
})
export class OrdiniVenditaShowColumnsComponent implements OnInit {
  // Colonne attive, in ordine di visualizzazione (riordinabili). RifOrdine resta bloccato e primo.
  activeColumns: SelectableColumn[] = [];
  // Colonne disponibili (non attive) raggruppate per categoria, per restare scansionabili (~50 voci).
  availableGroups: CategoryGroup[] = [];

  accountOrganizzazione: AccountOrganizzazione;
  loading = false;

  private allColumns: SelectableColumn[] = [];

  constructor(
    private saveMessage: SaveMessage,
    private organizzazioniService: OrganizzazioniService,
    private campiUtenteService: CampiUtenteService,
    private modalCtrl: ModalController
  ) {}

  async ngOnInit() {
    this.accountOrganizzazione = this.organizzazioniService.currentAccountSettingsSubject.value;
    const defs = await this.loadCampiUtenteDefinizioni();
    const config = this.organizzazioniService.venditaShowColumnsConfigWith(defs);
    this.allColumns = config.map(c => this.toSelectable(c));
    this.initFromConfig(config);
  }

  // Riuso del servizio già usato dalla home Ordini Clienti. Errore ⇒ nessun Campo Utente.
  private async loadCampiUtenteDefinizioni(): Promise<CampoUtenteDef[]> {
    const orgId = this.organizzazioniService.organizzazioneId;
    if (!orgId) {
      return [];
    }
    try {
      const defs = await this.campiUtenteService.getDefinizioni(orgId, true);
      return (defs ?? []).filter(d => d.isActive);
    } catch (err) {
      console.error('[ordini-vendita-show-columns] caricamento Campi Utente fallito', err);
      return [];
    }
  }

  private toSelectable(c: GroupColumn): SelectableColumn {
    const category = c.columnName.startsWith('Cu_')
      ? 'Campi Utente'
      : (getOvColumnMeta(c.columnName)?.category ?? 'Altro');
    return {columnName: c.columnName, viewName: c.viewName, category};
  }

  private initFromConfig(config: GroupColumn[]): void {
    const byName = new Map(this.allColumns.map(c => [c.columnName, c]));
    this.activeColumns = config
      .filter(c => c.active)
      .sort((a, b) => a.order - b.order)
      .map(c => byName.get(c.columnName)!)
      .filter(Boolean);
    this.ensureLockFirst();
    this.rebuildAvailable();
  }

  // RifOrdine sempre presente e in prima posizione (vincolo esistente).
  private ensureLockFirst(): void {
    const lock = this.allColumns.find(c => c.columnName === LOCK_COLUMN);
    if (!lock) {
      return;
    }
    this.activeColumns = this.activeColumns.filter(c => c.columnName !== LOCK_COLUMN);
    this.activeColumns.unshift(lock);
  }

  private rebuildAvailable(): void {
    const activeKeys = new Set(this.activeColumns.map(c => c.columnName));
    const groups = new Map<string, SelectableColumn[]>();
    for (const col of this.allColumns) {
      if (activeKeys.has(col.columnName)) {
        continue;
      }
      const list = groups.get(col.category);
      if (list) {
        list.push(col);
      } else {
        groups.set(col.category, [col]);
      }
    }
    this.availableGroups = Array.from(groups, ([category, columns]) => ({category, columns}));
  }

  isLocked(col: SelectableColumn): boolean {
    return col.columnName === LOCK_COLUMN;
  }

  addColumn(col: SelectableColumn): void {
    this.activeColumns.push(col);
    this.rebuildAvailable();
  }

  removeColumn(col: SelectableColumn): void {
    if (this.isLocked(col)) {
      return;
    }
    this.activeColumns = this.activeColumns.filter(c => c.columnName !== col.columnName);
    this.rebuildAvailable();
  }

  cancel = () => this.modalCtrl.dismiss(false, 'cancel');

  handleReorder(ev: CustomEvent) {
    this.activeColumns = ev.detail.complete(this.activeColumns);
    this.ensureLockFirst();
  }

  trackByName = (_: number, col: SelectableColumn) => col.columnName;
  trackByCategory = (_: number, g: CategoryGroup) => g.category;

  resetDefault() {
    this.loading = true;
    this.accountOrganizzazione.colonneOrdiniCliente = null;
    this.organizzazioniService.saveAccountOrganizzazioneAsync(this.accountOrganizzazione)
      .then((_acc: AccountOrganizzazione) => {
        this.organizzazioniService.setCurrentAccountSettingsValue(_acc);
        this.modalCtrl.dismiss(true, 'confirm');
      })
      .catch(() => this.saveMessage.messageError('Ci sono problemi con il salvataggio.'))
      .finally(() => this.loading = false);
  }

  succesSave() {
    this.loading = true;
    const activeColumns = this.activeColumns.map(c => c.columnName);
    if (!activeColumns.includes(LOCK_COLUMN)) {
      activeColumns.unshift(LOCK_COLUMN);
    }
    this.accountOrganizzazione.colonneOrdiniCliente = activeColumns.join(',');
    this.organizzazioniService.saveAccountOrganizzazioneAsync(this.accountOrganizzazione)
      .then((_acc: AccountOrganizzazione) => {
        this.organizzazioniService.setCurrentAccountSettingsValue(_acc);
        this.modalCtrl.dismiss(true, 'confirm');
      })
      .catch(() => this.saveMessage.messageError('Ci sono problemi con il salvataggio.'))
      .finally(() => this.loading = false);
  }
}
